import type { PerspectiveCamera } from 'three';
import type { WorldSignals } from './dev/Conductor.js';

/**
 * Camera breathing and slow drift.
 *
 * Every motion here is layered sines with periods that share no common multiple,
 * so the camera never returns to a pose it has held before. That is the whole
 * trick behind "alive from time alone": a single sine reads as a mechanism, three
 * detuned ones read as something breathing.
 *
 * Amplitudes are deliberately small. §12 of the requirements is explicit that
 * camera movement must not interfere with precision play, so this establishes the
 * ceiling now rather than discovering it later — the mandala's centre never leaves
 * the middle of the frame, and the framing distance stays anchored to
 * `PlayField.fitDistance`, so the playable region is unaffected by any of it.
 */

export interface CameraDirectorOptions {
  /** Dolly amplitude as a fraction of framing distance. */
  readonly breathAmount?: number;
  /** Peak yaw/pitch drift, radians. */
  readonly driftAmount?: number;
  /** Peak roll, radians. */
  readonly rollAmount?: number;
}

export class CameraDirector {
  private readonly breathAmount: number;
  private readonly driftAmount: number;
  private readonly rollAmount: number;

  constructor(options: CameraDirectorOptions = {}) {
    this.breathAmount = options.breathAmount ?? 0.055;
    this.driftAmount = options.driftAmount ?? 0.052;
    this.rollAmount = options.rollAmount ?? 0.021;
  }

  /**
   * @param framingDistance Distance at which the play field is fully framed.
   *   Breathing modulates around this, never below it by more than `breathAmount`,
   *   so the field cannot be cropped by the animation.
   */
  update(camera: PerspectiveCamera, signals: WorldSignals, framingDistance: number): void {
    const time = signals.time;

    // Dolly. The second harmonic is a third of the amplitude at an unrelated
    // period, which is what stops the motion feeling metronomic.
    const breath =
      Math.sin(time * 0.0764) * 0.75 + // ~82 s
      Math.sin(time * 0.1310) * 0.25; // ~48 s
    const distance = framingDistance * (1 + breath * this.breathAmount);

    // Drift traces a slow Lissajous rather than a circle, so the path never
    // closes and no two passes are identical.
    const yaw = Math.sin(time * 0.0431) * this.driftAmount;
    const pitch = Math.sin(time * 0.0577 + 1.7) * this.driftAmount * 0.72;

    camera.position.set(
      Math.sin(yaw) * distance,
      Math.sin(pitch) * distance,
      Math.cos(yaw) * Math.cos(pitch) * distance,
    );
    camera.lookAt(0, 0, 0);

    // Roll last: lookAt resets the up vector, so applying roll before it would be
    // overwritten.
    camera.rotation.z += Math.sin(time * 0.0338 + 0.6) * this.rollAmount;
  }
}
